import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { staffApi, unitsApi } from '../lib/api';
import { apiErrorMessage } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader,
  DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import {
  Users, Plus, Pencil, Trash2, Search, Phone, Mail, Truck,
  DollarSign, FileText, Download, X, Loader2, Briefcase, UserCog,
  Building2, MapPin,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency, getWeekStart, getWeekRange } from '../lib/utils';

const ROLE_OPTIONS = [
  { value: 'dispatcher', label: 'Dispatcher' },
  { value: 'independent_contractor', label: 'Independent Contractor' },
  { value: 'truck_owner', label: 'Truck Owner' },
  { value: 'owner_operator', label: 'Owner Operator' },
  { value: 'office_staff', label: 'Office Staff' },
  { value: 'mechanic', label: 'Mechanic' },
  { value: 'other', label: 'Other (custom name)' },
];

const PAY_TYPE_OPTIONS = [
  { value: 'salary', label: 'Salary (fixed weekly amount)', description: 'Same amount every week, regardless of loads' },
  { value: 'percentage_gross', label: '% of unit GROSS revenue', description: 'X% of total load payouts on linked units (before fees & deductions)' },
  { value: 'percentage_net', label: '% of unit NET payout', description: 'X% of (gross − dispatch fees − unit deductions). The cleanest option for owner-operators.' },
  { value: 'per_load_flat', label: 'Flat per load', description: '$X per load on linked units' },
  { value: 'per_mile', label: 'Per mile', description: '$X per mile on linked units' },
  { value: 'flat_per_active_week', label: 'Flat per active week', description: 'Pay $X if the unit had at least one load that week, otherwise $0' },
  { value: 'hourly', label: 'Hourly', description: 'Hours × rate; enter hours when generating each paystub' },
  { value: 'custom_amount', label: 'Custom amount per paystub', description: 'Enter the gross amount manually each time' },
];

const ROLE_BADGE_STYLES = {
  dispatcher: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300',
  independent_contractor: 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300',
  truck_owner: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300',
  owner_operator: 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300',
  office_staff: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  mechanic: 'bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300',
  other: 'bg-purple-100 text-purple-700 dark:bg-purple-900/40 dark:text-purple-300',
};

const blankStaff = {
  name: '', role: 'dispatcher', custom_role: '',
  email: '', phone: '', street_address: '', city: '', state: '', zipcode: '',
  ssn_last_four: '', status: 'active', unit_ids: [],
  pay_type: 'salary', pay_rate: 0, notes: '', start_date: '',
};

export default function Staff() {
  const [staff, setStaff] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');

  const [editing, setEditing] = useState(null); // staff being edited (in dialog)
  const [editForm, setEditForm] = useState(blankStaff);
  const [editOpen, setEditOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [selected, setSelected] = useState(null); // staff for the side panel
  const [weekStart, setWeekStart] = useState(getWeekStart());
  const [endDate, setEndDate] = useState('');  // optional — when set, paystub spans week_start → end_date
  const [paySummary, setPaySummary] = useState(null);
  const [overrideGross, setOverrideGross] = useState('');
  const [overrideHours, setOverrideHours] = useState('');
  const [generating, setGenerating] = useState(false);

  // Inline addon/deduction creation
  const [newAddon, setNewAddon] = useState({ name: '', amount: '', addon_type: 'bonus' });
  const [newDed, setNewDed] = useState({ name: '', amount: '' });

  const loadStaff = useCallback(async () => {
    setLoading(true);
    try {
      const r = await staffApi.getAll();
      setStaff(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUnits = useCallback(async () => {
    try {
      const r = await unitsApi.getAll();
      setUnits(r.data || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  useEffect(() => {
    loadStaff();
    loadUnits();
  }, [loadStaff, loadUnits]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return staff
      .filter(s => roleFilter === 'all' || s.role === roleFilter)
      .filter(s => {
        if (!q) return true;
        return [s.name, s.email, s.phone, s.custom_role].filter(Boolean)
          .some(v => v.toLowerCase().includes(q));
      });
  }, [staff, search, roleFilter]);

  const openCreate = () => {
    setEditing(null);
    setEditForm(blankStaff);
    setEditOpen(true);
  };

  const openEdit = (s) => {
    setEditing(s);
    setEditForm({
      ...blankStaff,
      ...s,
      pay_rate: s.pay_rate ?? 0,
      unit_ids: s.unit_ids || [],
      custom_role: s.custom_role || '',
    });
    setEditOpen(true);
  };

  const submit = async () => {
    if (!editForm.name.trim()) {
      toast.error('Name is required');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        pay_rate: parseFloat(editForm.pay_rate) || 0,
        unit_ids: editForm.unit_ids || [],
      };
      if (editing) {
        await staffApi.update(editing.id, payload);
        toast.success('Staff updated');
      } else {
        await staffApi.create(payload);
        toast.success('Staff created');
      }
      setEditOpen(false);
      await loadStaff();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Delete ${s.name}? This also removes their paystub addons/deductions.`)) return;
    try {
      await staffApi.delete(s.id);
      toast.success('Removed');
      if (selected?.id === s.id) setSelected(null);
      await loadStaff();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete');
    }
  };

  // ---------- Pay panel ----------
  const loadPaySummary = useCallback(async (s, ws) => {
    if (!s) return;
    try {
      const opts = {};
      if (overrideGross !== '') opts.override_gross = parseFloat(overrideGross) || 0;
      if (overrideHours !== '') opts.override_hours = parseFloat(overrideHours) || 0;
      const r = await staffApi.getPaySummary(s.id, ws, opts);
      setPaySummary(r.data);
    } catch (e) {
      console.error(e);
    }
  }, [overrideGross, overrideHours]);

  useEffect(() => {
    if (selected) loadPaySummary(selected, weekStart);
    else setPaySummary(null);
  }, [selected, weekStart, loadPaySummary]);

  const downloadPaystub = async () => {
    if (!selected) return;
    setGenerating(true);
    try {
      const opts = {};
      if (endDate && endDate !== weekStart) opts.end_date = endDate;
      if (overrideGross !== '') opts.override_gross = parseFloat(overrideGross) || 0;
      if (overrideHours !== '') opts.override_hours = parseFloat(overrideHours) || 0;
      const res = await staffApi.getPaystub(selected.id, weekStart, opts);
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const fname = (endDate && endDate !== weekStart)
        ? `Paystub_${selected.name.replace(/\s+/g, '_')}_${weekStart}_to_${endDate}.pdf`
        : `Paystub_${selected.name.replace(/\s+/g, '_')}_${weekStart}.pdf`;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Paystub downloaded');
    } catch (e) {
      toast.error('Failed to generate paystub');
    } finally {
      setGenerating(false);
    }
  };

  const addAddon = async () => {
    if (!selected || !newAddon.name || !newAddon.amount) return;
    try {
      await staffApi.addAddon(selected.id, {
        ...newAddon,
        amount: parseFloat(newAddon.amount),
        week_start: weekStart,
      });
      setNewAddon({ name: '', amount: '', addon_type: 'bonus' });
      await loadPaySummary(selected, weekStart);
    } catch (e) {
      toast.error('Failed to add bonus');
    }
  };

  const removeAddon = async (id) => {
    try {
      await staffApi.deleteAddon(id);
      await loadPaySummary(selected, weekStart);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to remove add-on'));
    }
  };

  const addDeduction = async () => {
    if (!selected || !newDed.name || !newDed.amount) return;
    try {
      await staffApi.addDeduction(selected.id, {
        ...newDed,
        amount: parseFloat(newDed.amount),
        week_start: weekStart,
      });
      setNewDed({ name: '', amount: '' });
      await loadPaySummary(selected, weekStart);
    } catch (e) {
      toast.error('Failed to add deduction');
    }
  };

  const removeDeduction = async (id) => {
    try {
      await staffApi.deleteDeduction(id);
      await loadPaySummary(selected, weekStart);
    } catch (e) {
      toast.error(apiErrorMessage(e, 'Failed to remove deduction'));
    }
  };

  // ---------- Render ----------
  const unitsLookup = useMemo(() =>
    Object.fromEntries(units.map(u => [u.id, u])), [units]);

  const isUnitDependent = ['percentage', 'percentage_gross', 'percentage_net', 'per_load_flat', 'per_mile', 'flat_per_active_week'].includes(editForm.pay_type);

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8" data-testid="staff-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold tracking-tight font-manrope flex items-center gap-2">
            <Briefcase className="h-7 w-7 text-blue-600" /> Staff
          </h1>
          <p className="text-sm text-muted-foreground">
            Dispatchers, contractors, truck owners, owner operators & custom roles. Generate paystubs, attach to one or many units.
          </p>
        </div>
        <Button onClick={openCreate} data-testid="add-staff-btn">
          <Plus className="h-4 w-4 mr-1" /> Add Staff
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, phone…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="staff-search"
          />
        </div>
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="w-full sm:w-[220px]" data-testid="staff-role-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            {ROLE_OPTIONS.map(r => (
              <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        {/* Staff list */}
        <Card className={selected ? 'lg:col-span-2' : 'lg:col-span-5'}>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-8 text-center text-muted-foreground">Loading…</div>
            ) : filtered.length === 0 ? (
              <div className="p-8 text-center">
                <Users className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm font-semibold">No staff yet</p>
                <p className="text-xs text-muted-foreground">Click "Add Staff" to add your first dispatcher, contractor, or owner operator.</p>
              </div>
            ) : (
              <ul className="divide-y" data-testid="staff-list">
                {filtered.map(s => {
                  const isActive = selected?.id === s.id;
                  const roleLabel = s.role === 'other' && s.custom_role
                    ? s.custom_role
                    : (ROLE_OPTIONS.find(r => r.value === s.role)?.label || s.role);
                  return (
                    <li
                      key={s.id}
                      className={`p-4 cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-900/40 ${isActive ? 'bg-blue-50 dark:bg-blue-950/20' : ''}`}
                      onClick={() => setSelected(s)}
                      data-testid={`staff-row-${s.id}`}
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-semibold truncate">{s.name}</span>
                            <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${ROLE_BADGE_STYLES[s.role] || ROLE_BADGE_STYLES.other}`}>
                              {roleLabel}
                            </span>
                            {s.status === 'inactive' && (
                              <Badge variant="outline" className="text-[10px]">Inactive</Badge>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-muted-foreground space-y-0.5">
                            {s.email && <div className="flex items-center gap-1"><Mail className="h-3 w-3" />{s.email}</div>}
                            {s.phone && <div className="flex items-center gap-1"><Phone className="h-3 w-3" />{s.phone}</div>}
                            {(s.unit_ids?.length || 0) > 0 && (
                              <div className="flex items-center gap-1">
                                <Truck className="h-3 w-3" />
                                {s.unit_ids.map(id => unitsLookup[id]?.unit_number).filter(Boolean).join(', ') || `${s.unit_ids.length} unit(s)`}
                              </div>
                            )}
                            {s.pay_type === 'salary' && (
                              <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{formatCurrency(s.pay_rate || 0)}/wk salary</div>
                            )}
                            {(s.pay_type === 'percentage' || s.pay_type === 'percentage_gross') && (
                              <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{s.pay_rate || 0}% of unit gross</div>
                            )}
                            {s.pay_type === 'percentage_net' && (
                              <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{s.pay_rate || 0}% of unit NET</div>
                            )}
                            {s.pay_type === 'per_mile' && (
                              <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" />${s.pay_rate || 0}/mile</div>
                            )}
                            {s.pay_type === 'per_load_flat' && (
                              <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" />${s.pay_rate || 0}/load</div>
                            )}
                            {s.pay_type === 'flat_per_active_week' && (
                              <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" />{formatCurrency(s.pay_rate || 0)}/active week</div>
                            )}
                            {s.pay_type === 'hourly' && (
                              <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" />${s.pay_rate || 0}/hr</div>
                            )}
                            {s.pay_type === 'custom_amount' && (
                              <div className="flex items-center gap-1"><DollarSign className="h-3 w-3" />Custom amount per paystub</div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="ghost" size="sm"
                            onClick={(e) => { e.stopPropagation(); openEdit(s); }}
                            data-testid={`edit-staff-${s.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost" size="sm"
                            onClick={(e) => { e.stopPropagation(); remove(s); }}
                            data-testid={`delete-staff-${s.id}`}
                          >
                            <Trash2 className="h-4 w-4 text-rose-500" />
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Detail / Pay panel */}
        {selected && (
          <Card className="lg:col-span-3" data-testid="staff-detail-panel">
            <CardContent className="p-5 space-y-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <div className="flex items-center gap-2 flex-wrap">
                    <h2 className="text-xl font-bold font-manrope">{selected.name}</h2>
                    <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${ROLE_BADGE_STYLES[selected.role]}`}>
                      {selected.role === 'other' && selected.custom_role
                        ? selected.custom_role
                        : ROLE_OPTIONS.find(r => r.value === selected.role)?.label}
                    </span>
                  </div>
                  {selected.start_date && (
                    <p className="text-xs text-muted-foreground mt-1">Started {selected.start_date}</p>
                  )}
                </div>
                <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
                  <X className="h-4 w-4" />
                </Button>
              </div>

              <Tabs defaultValue="overview" className="w-full">
                <TabsList className="w-full">
                  <TabsTrigger value="overview" className="flex-1">Overview</TabsTrigger>
                  <TabsTrigger value="paystub" className="flex-1">Pay & Stubs</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="space-y-3 pt-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Field icon={Mail} label="Email" value={selected.email} />
                    <Field icon={Phone} label="Phone" value={selected.phone} />
                    <Field
                      icon={MapPin} label="Address"
                      value={[selected.street_address, selected.city, selected.state, selected.zipcode].filter(Boolean).join(', ')}
                    />
                    <Field icon={Building2} label="Status" value={selected.status} />
                  </div>
                  <div>
                    <Label className="text-xs uppercase text-muted-foreground">Linked Units</Label>
                    <div className="mt-1 flex flex-wrap gap-1.5">
                      {(selected.unit_ids || []).length === 0 ? (
                        <span className="text-xs text-muted-foreground italic">Not linked to any unit (standalone pay)</span>
                      ) : (
                        selected.unit_ids.map(id => (
                          <span key={id} className="px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-xs font-mono dark:bg-blue-950/30 dark:text-blue-300">
                            {unitsLookup[id]?.unit_number || id.slice(0, 8)}
                          </span>
                        ))
                      )}
                    </div>
                  </div>
                  {selected.notes && (
                    <div>
                      <Label className="text-xs uppercase text-muted-foreground">Notes</Label>
                      <p className="text-sm whitespace-pre-wrap mt-1">{selected.notes}</p>
                    </div>
                  )}
                </TabsContent>

                <TabsContent value="paystub" className="space-y-4 pt-3">
                  {/* Date range picker — single week or full range */}
                  <div className="rounded-lg border bg-slate-50/40 dark:bg-slate-900/20 p-3 space-y-2">
                    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground">
                      Paystub date range
                    </Label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label className="text-[10px] text-muted-foreground">From</Label>
                        <Input
                          type="date"
                          value={weekStart}
                          onChange={(e) => setWeekStart(e.target.value)}
                          data-testid="staff-week-input"
                        />
                      </div>
                      <div>
                        <Label className="text-[10px] text-muted-foreground">To (optional)</Label>
                        <Input
                          type="date"
                          value={endDate}
                          onChange={(e) => setEndDate(e.target.value)}
                          placeholder="Leave blank for single week"
                          data-testid="staff-end-input"
                        />
                      </div>
                    </div>
                    <div className="flex items-center justify-between text-[11px]">
                      <span className="text-muted-foreground">
                        {endDate && endDate !== weekStart
                          ? <>Spans <strong>{getWeekRange(weekStart)}</strong> → <strong>{endDate}</strong> — one page per week</>
                          : <>Single week: <strong>{getWeekRange(weekStart)}</strong></>}
                      </span>
                      {endDate && (
                        <Button variant="ghost" size="sm" className="h-6 text-[11px]"
                                onClick={() => setEndDate('')}>
                          Clear range
                        </Button>
                      )}
                    </div>
                    <Button onClick={downloadPaystub} disabled={generating} className="w-full" data-testid="staff-download-paystub">
                      {generating ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}
                      Generate Paystub PDF
                    </Button>
                  </div>

                  {/* Override fields for hourly / custom_amount */}
                  {selected.pay_type === 'hourly' && (
                    <div>
                      <Label className="text-xs">Hours worked this week</Label>
                      <Input
                        type="number" step="0.25"
                        placeholder="e.g. 40"
                        value={overrideHours}
                        onChange={(e) => setOverrideHours(e.target.value)}
                        data-testid="staff-hours-input"
                      />
                    </div>
                  )}
                  {selected.pay_type === 'custom_amount' && (
                    <div>
                      <Label className="text-xs">Gross amount this week</Label>
                      <Input
                        type="number" step="0.01"
                        placeholder="e.g. 1200.00"
                        value={overrideGross}
                        onChange={(e) => setOverrideGross(e.target.value)}
                        data-testid="staff-gross-input"
                      />
                    </div>
                  )}

                  {/* Summary box */}
                  {paySummary && (
                    <div className="rounded-lg border bg-slate-50 dark:bg-slate-900/40 p-3 grid grid-cols-3 gap-2 text-center text-sm">
                      <div>
                        <div className="text-[10px] uppercase text-muted-foreground">Gross</div>
                        <div className="font-bold text-emerald-700">{formatCurrency(paySummary.gross_pay)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-muted-foreground">Deductions</div>
                        <div className="font-bold text-rose-600">−{formatCurrency(paySummary.total_deductions)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase text-muted-foreground">Net Pay</div>
                        <div className="font-bold text-blue-700">{formatCurrency(paySummary.net_pay)}</div>
                      </div>
                      {paySummary.loads_count > 0 && (
                        <div className="col-span-3 text-[11px] text-muted-foreground border-t pt-2 mt-1">
                          {paySummary.loads_count} load(s) · {paySummary.miles?.toLocaleString()} mi · unit revenue {formatCurrency(paySummary.unit_revenue)}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Bonuses / Addons */}
                  <Section title="Bonuses & reimbursements (this week)">
                    {paySummary?.addons?.map(a => (
                      <Row
                        key={a.id}
                        left={<><span className="font-medium">{a.name}</span> <span className="text-[10px] uppercase text-muted-foreground ml-1">{a.addon_type}</span></>}
                        right={<span className="text-emerald-700">+{formatCurrency(a.amount)}</span>}
                        onRemove={() => removeAddon(a.id)}
                      />
                    ))}
                    <div className="flex gap-1 items-end pt-1">
                      <div className="flex-1"><Input placeholder="Bonus name" value={newAddon.name} onChange={e => setNewAddon({ ...newAddon, name: e.target.value })} className="h-8 text-xs" /></div>
                      <div className="w-[100px]"><Input placeholder="$" type="number" step="0.01" value={newAddon.amount} onChange={e => setNewAddon({ ...newAddon, amount: e.target.value })} className="h-8 text-xs" /></div>
                      <Button size="sm" variant="outline" onClick={addAddon}><Plus className="h-3 w-3" /></Button>
                    </div>
                  </Section>

                  <Section title="Deductions (this week)">
                    {paySummary?.deductions?.map(d => (
                      <Row
                        key={d.id}
                        left={<span className="font-medium">{d.name}</span>}
                        right={<span className="text-rose-600">−{formatCurrency(d.amount)}</span>}
                        onRemove={() => removeDeduction(d.id)}
                      />
                    ))}
                    <div className="flex gap-1 items-end pt-1">
                      <div className="flex-1"><Input placeholder="Deduction name" value={newDed.name} onChange={e => setNewDed({ ...newDed, name: e.target.value })} className="h-8 text-xs" /></div>
                      <div className="w-[100px]"><Input placeholder="$" type="number" step="0.01" value={newDed.amount} onChange={e => setNewDed({ ...newDed, amount: e.target.value })} className="h-8 text-xs" /></div>
                      <Button size="sm" variant="outline" onClick={addDeduction}><Plus className="h-3 w-3" /></Button>
                    </div>
                  </Section>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create / Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-[640px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-manrope">{editing ? 'Edit Staff' : 'Add Staff'}</DialogTitle>
            <DialogDescription>
              {editing ? 'Update this person\'s details, role, and pay setup.' : 'Add a dispatcher, contractor, owner operator or custom role. They can be linked to one or many units, or stand alone with a salary.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Full name *</Label>
                <Input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })} data-testid="staff-form-name" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={editForm.role} onValueChange={v => setEditForm({ ...editForm, role: v })}>
                  <SelectTrigger data-testid="staff-form-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(r => (
                      <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {editForm.role === 'other' && (
                <div>
                  <Label>Custom role name *</Label>
                  <Input
                    value={editForm.custom_role}
                    onChange={e => setEditForm({ ...editForm, custom_role: e.target.value })}
                    placeholder="e.g. Recruiter, Safety Mgr"
                  />
                </div>
              )}
              <div>
                <Label>Email</Label>
                <Input value={editForm.email || ''} onChange={e => setEditForm({ ...editForm, email: e.target.value })} />
              </div>
              <div>
                <Label>Phone</Label>
                <Input value={editForm.phone || ''} onChange={e => setEditForm({ ...editForm, phone: e.target.value })} />
              </div>
              <div className="col-span-2">
                <Label>Street address</Label>
                <Input value={editForm.street_address || ''} onChange={e => setEditForm({ ...editForm, street_address: e.target.value })} />
              </div>
              <div><Label>City</Label><Input value={editForm.city || ''} onChange={e => setEditForm({ ...editForm, city: e.target.value })} /></div>
              <div><Label>State</Label><Input value={editForm.state || ''} onChange={e => setEditForm({ ...editForm, state: e.target.value })} /></div>
              <div><Label>ZIP</Label><Input value={editForm.zipcode || ''} onChange={e => setEditForm({ ...editForm, zipcode: e.target.value })} /></div>
              <div><Label>SSN last 4</Label><Input maxLength={4} value={editForm.ssn_last_four || ''} onChange={e => setEditForm({ ...editForm, ssn_last_four: e.target.value })} /></div>
              <div>
                <Label>Status</Label>
                <Select value={editForm.status} onValueChange={v => setEditForm({ ...editForm, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Start date</Label>
                <Input type="date" value={editForm.start_date || ''} onChange={e => setEditForm({ ...editForm, start_date: e.target.value })} />
              </div>
            </div>

            {/* Pay structure */}
            <div className="rounded-lg border p-3 space-y-3 bg-slate-50/40 dark:bg-slate-900/20">
              <div className="flex items-center gap-2">
                <UserCog className="h-4 w-4" />
                <Label className="text-sm font-semibold uppercase tracking-wide">Pay setup</Label>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Pay type</Label>
                  <Select value={editForm.pay_type} onValueChange={v => setEditForm({ ...editForm, pay_type: v })}>
                    <SelectTrigger data-testid="staff-form-pay-type"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {PAY_TYPE_OPTIONS.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {PAY_TYPE_OPTIONS.find(o => o.value === editForm.pay_type)?.description}
                  </p>
                </div>
                {editForm.pay_type !== 'custom_amount' && (
                  <div>
                    <Label>Rate / amount</Label>
                    <Input
                      type="number" step="0.01"
                      value={editForm.pay_rate}
                      onChange={e => setEditForm({ ...editForm, pay_rate: e.target.value })}
                      placeholder={
                        editForm.pay_type === 'percentage_gross' || editForm.pay_type === 'percentage_net' || editForm.pay_type === 'percentage'
                          ? 'e.g. 25 (percent)'
                          : editForm.pay_type === 'salary' || editForm.pay_type === 'flat_per_active_week'
                          ? 'e.g. 1200 (dollars)'
                          : 'amount'
                      }
                    />
                  </div>
                )}
              </div>

              {/* Unit linking — multi-select */}
              <div>
                <Label>Linked units {isUnitDependent && <span className="text-rose-500">*</span>}</Label>
                <p className="text-[10px] text-muted-foreground mb-2">
                  {isUnitDependent
                    ? 'Pay is calculated from loads on these units. Pick one or many.'
                    : 'Optional. For salary/hourly/custom, units are informational only.'}
                </p>
                <div className="rounded-lg border p-2 max-h-[160px] overflow-y-auto">
                  {units.length === 0 ? (
                    <p className="text-xs text-muted-foreground italic">No units yet</p>
                  ) : (
                    <div className="grid grid-cols-2 gap-1">
                      {units.map(u => {
                        const checked = (editForm.unit_ids || []).includes(u.id);
                        return (
                          <label key={u.id} className={`flex items-center gap-2 px-2 py-1 rounded cursor-pointer text-sm ${checked ? 'bg-blue-50 dark:bg-blue-950/30' : 'hover:bg-slate-50 dark:hover:bg-slate-900/40'}`}>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={e => {
                                const cur = editForm.unit_ids || [];
                                setEditForm({
                                  ...editForm,
                                  unit_ids: e.target.checked
                                    ? [...cur, u.id]
                                    : cur.filter(x => x !== u.id),
                                });
                              }}
                            />
                            <span className="font-mono">{u.unit_number}</span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <Label>Notes</Label>
              <Input value={editForm.notes || ''} onChange={e => setEditForm({ ...editForm, notes: e.target.value })} />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="staff-form-submit">
              {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null}
              {editing ? 'Save changes' : 'Add staff'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ icon: Icon, label, value }) => (
  <div>
    <Label className="text-[10px] uppercase text-muted-foreground flex items-center gap-1">
      <Icon className="h-3 w-3" />{label}
    </Label>
    <div className="text-sm mt-0.5 truncate" title={value || ''}>{value || <span className="text-muted-foreground italic">—</span>}</div>
  </div>
);

const Section = ({ title, children }) => (
  <div className="space-y-1">
    <Label className="text-[10px] uppercase tracking-wide text-muted-foreground flex items-center gap-1">
      <FileText className="h-3 w-3" />{title}
    </Label>
    <div className="space-y-1">{children}</div>
  </div>
);

const Row = ({ left, right, onRemove }) => (
  <div className="flex items-center justify-between text-sm border-b py-1">
    <div className="flex-1 min-w-0">{left}</div>
    <div className="flex items-center gap-2">
      {right}
      {onRemove && (
        <Button variant="ghost" size="sm" onClick={onRemove} className="h-6 w-6 p-0">
          <X className="h-3 w-3" />
        </Button>
      )}
    </div>
  </div>
);
